/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package demo

import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.flowOf
import studio.oddpods.cauce.core.network.auth.AuthService
import studio.oddpods.cauce.core.network.auth.SessionStatus
import studio.oddpods.cauce.core.data.model.LogoutEvent
import studio.oddpods.cauce.core.data.model.LogoutReason
import studio.oddpods.cauce.core.data.repository.UserDataRepository
import studio.oddpods.cauce.core.data.repository.UserLogoutManager
import studio.oddpods.cauce.core.data.repository.cauce.DashboardRepository
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.data.repository.cauce.PaymentRepository
import studio.oddpods.cauce.core.data.repository.cauce.ProfileRepository
import studio.oddpods.cauce.core.data.repository.cauce.ReminderRepository
import studio.oddpods.cauce.core.data.repository.cauce.ReminderSentEntry
import studio.oddpods.cauce.core.data.repository.cauce.SessionRepository
import studio.oddpods.cauce.core.data.repository.notification.NotificationSettingsRepository
import studio.oddpods.cauce.core.model.DarkThemeConfig
import studio.oddpods.cauce.core.model.LanguageConfig
import studio.oddpods.cauce.core.model.ThemeBrand
import studio.oddpods.cauce.core.model.UserData
import studio.oddpods.cauce.core.model.dashboard.DashboardSummary
import studio.oddpods.cauce.core.model.dashboard.PatientPaymentSummary
import studio.oddpods.cauce.core.model.dashboard.PatientStatus
import studio.oddpods.cauce.core.model.display.DisplayPreferences
import studio.oddpods.cauce.core.model.notification.NotificationPreferences
import studio.oddpods.cauce.core.model.patient.CreatePatientRequest
import studio.oddpods.cauce.core.model.patient.Patient
import studio.oddpods.cauce.core.model.payment.CreatePaymentRequest
import studio.oddpods.cauce.core.model.payment.Payment
import studio.oddpods.cauce.core.model.payment.PaymentMethod
import studio.oddpods.cauce.core.model.profile.OnboardingStep
import studio.oddpods.cauce.core.model.profile.Profile
import studio.oddpods.cauce.core.model.reminder.PendingReminder
import studio.oddpods.cauce.core.model.session.CreateSessionRequest
import studio.oddpods.cauce.core.model.session.PaymentStatus
import studio.oddpods.cauce.core.model.session.UpdateSessionRequest
import studio.oddpods.cauce.core.model.session.Session
import studio.oddpods.cauce.core.model.session.SessionType

class DemoUserDataRepository : UserDataRepository {
    private val _userData = MutableStateFlow(UserData.DEFAULT)
    override val userData: StateFlow<UserData> = _userData.asStateFlow()
    override val authToken: String? = null
    override val passcode: String = ""
    override val observeLanguage: Flow<LanguageConfig> = flowOf(LanguageConfig.DEFAULT)
    override val observeDarkThemeConfig: Flow<DarkThemeConfig> = flowOf(DarkThemeConfig.FOLLOW_SYSTEM)
    override val observeDynamicColorPreference: Flow<Boolean> = flowOf(false)
    override val observeScreenCapturePreference: Flow<Boolean> = flowOf(false)
    override val observeOnboardingBannerDismissed: Flow<Boolean> = flowOf(true)
    override val observeDisplayPreferences: Flow<DisplayPreferences> = flowOf(DisplayPreferences.DEFAULT)

    override suspend fun setLanguage(language: LanguageConfig) = Unit
    override suspend fun setThemeBrand(themeBrand: ThemeBrand) = Unit
    override suspend fun setDarkThemeConfig(darkThemeConfig: DarkThemeConfig) = Unit
    override suspend fun setDynamicColorPreference(useDynamicColor: Boolean) = Unit
    override suspend fun setIsAuthenticated(isAuthenticated: Boolean) = Unit
    override suspend fun setIsUnlocked(isUnlocked: Boolean) = Unit
    override suspend fun setIsPasscodeEnabled(isPasscodeEnabled: Boolean) = Unit
    override suspend fun setIsBiometricsEnabled(isBiometricsEnabled: Boolean) = Unit
    override suspend fun setShowOnboarding(showOnboarding: Boolean) = Unit
    override suspend fun setFirstTimeState(firstTimeState: Boolean) = Unit
    override suspend fun setPasscode(passcode: String) = Unit
    override suspend fun setOnboardingBannerDismissed(dismissed: Boolean) = Unit
    override suspend fun setDisplayPreferences(prefs: DisplayPreferences) = Unit
    override suspend fun clearUserData() = Unit
}

class DemoProfileRepository : ProfileRepository {
    override suspend fun getCurrentProfile(): Result<Profile> = Result.success(
        Profile(
            id = "demo-profile",
            displayName = "Dr. Demo",
            tier = "basic",
            defaultSessionFee = 300000L,
            onboardingStep = OnboardingStep.COMPLETED,
            onboardingCompleted = true,
        ),
    )

    override suspend fun updateDisplayNameAndFee(displayName: String, defaultFeeCentavos: Long): Result<Unit> =
        Result.success(Unit)
    override suspend fun setOnboardingStep(step: OnboardingStep): Result<Unit> = Result.success(Unit)
    override suspend fun completeOnboarding(): Result<Unit> = Result.success(Unit)
    override suspend fun updateReminderSettings(
        scheduleDays: List<Int>,
        maxReminders: Int,
        template: String,
    ): Result<Unit> = Result.success(Unit)
}

private val demoPatients = listOf(
    Patient(
        id = "demo-patient-1",
        name = "Valentina Rodríguez",
        phone = "+5491123456789",
        email = "valentina.rodriguez@email.com",
        defaultFee = 300000L,
        notes = null,
        remindersPaused = false,
        active = true,
        createdAt = "2026-01-15T10:00:00Z",
        updatedAt = "2026-04-01T10:00:00Z",
    ),
    Patient(
        id = "demo-patient-2",
        name = "Martín García",
        phone = "+5491134567890",
        email = null,
        defaultFee = 300000L,
        notes = null,
        remindersPaused = false,
        active = true,
        createdAt = "2026-02-01T09:00:00Z",
        updatedAt = "2026-04-10T09:00:00Z",
    ),
    Patient(
        id = "demo-patient-3",
        name = "Luciana Fernández",
        phone = "+5491145678901",
        email = "luciana.fernandez@email.com",
        defaultFee = 250000L,
        notes = "Turno fijo los martes",
        remindersPaused = false,
        active = true,
        createdAt = "2026-01-20T11:00:00Z",
        updatedAt = "2026-03-25T11:00:00Z",
    ),
    Patient(
        id = "demo-patient-4",
        name = "Santiago López",
        phone = "+5491156789012",
        email = null,
        defaultFee = 300000L,
        notes = null,
        remindersPaused = true,
        active = true,
        createdAt = "2026-03-01T14:00:00Z",
        updatedAt = "2026-04-15T14:00:00Z",
    ),
)

class DemoPatientRepository : PatientRepository {
    override fun getPatients(activeOnly: Boolean): Flow<List<Patient>> =
        flowOf(if (activeOnly) demoPatients.filter { it.active } else demoPatients)

    override suspend fun getPatient(patientId: String): Result<Patient> =
        Result.success(demoPatients.first { it.id == patientId })

    override suspend fun createPatient(request: CreatePatientRequest): Result<Patient> =
        Result.success(demoPatients.first())

    override suspend fun updatePatient(patientId: String, request: CreatePatientRequest): Result<Patient> =
        Result.success(demoPatients.first { it.id == patientId })

    override suspend fun deactivatePatient(patientId: String): Result<Unit> = Result.success(Unit)

    override fun observePatient(patientId: String): Flow<Patient?> =
        flowOf(demoPatients.firstOrNull { it.id == patientId })
}

private val demoSessions = listOf(
    Session(
        id = "demo-session-1",
        patientId = "demo-patient-1",
        sessionDate = "2026-04-02T10:00:00Z",
        fee = 300000L,
        sessionType = SessionType.REGULAR,
        paymentStatus = PaymentStatus.PAID,
        amountPaid = 300000L,
        createdAt = "2026-04-02T10:00:00Z",
    ),
    Session(
        id = "demo-session-2",
        patientId = "demo-patient-1",
        sessionDate = "2026-04-09T10:00:00Z",
        fee = 300000L,
        sessionType = SessionType.REGULAR,
        paymentStatus = PaymentStatus.PENDING,
        amountPaid = 0L,
        createdAt = "2026-04-09T10:00:00Z",
    ),
    Session(
        id = "demo-session-3",
        patientId = "demo-patient-2",
        sessionDate = "2026-04-05T11:00:00Z",
        fee = 300000L,
        sessionType = SessionType.REGULAR,
        paymentStatus = PaymentStatus.PAID,
        amountPaid = 300000L,
        createdAt = "2026-04-05T11:00:00Z",
    ),
    Session(
        id = "demo-session-4",
        patientId = "demo-patient-3",
        sessionDate = "2026-04-08T16:00:00Z",
        fee = 250000L,
        sessionType = SessionType.REGULAR,
        paymentStatus = PaymentStatus.PARTIAL,
        amountPaid = 150000L,
        createdAt = "2026-04-08T16:00:00Z",
    ),
    Session(
        id = "demo-session-5",
        patientId = "demo-patient-4",
        sessionDate = "2026-04-10T09:00:00Z",
        fee = 300000L,
        sessionType = SessionType.REGULAR,
        paymentStatus = PaymentStatus.PENDING,
        amountPaid = 0L,
        createdAt = "2026-04-10T09:00:00Z",
    ),
)

class DemoSessionRepository : SessionRepository {
    override fun getSessionsByPatient(patientId: String): Flow<List<Session>> =
        flowOf(demoSessions.filter { it.patientId == patientId })

    override suspend fun getSession(sessionId: String): Result<Session> =
        Result.success(demoSessions.first { it.id == sessionId })

    override suspend fun createSession(request: CreateSessionRequest): Result<Session> =
        Result.success(demoSessions.first())

    override suspend fun updateSession(sessionId: String, request: UpdateSessionRequest): Result<Session> =
        Result.success(demoSessions.first { it.id == sessionId })

    override suspend fun deleteSession(sessionId: String): Result<Unit> = Result.success(Unit)

    override fun getPendingConfirmations(): Flow<List<Session>> = flowOf(emptyList())
    override suspend fun confirmSession(sessionId: String): Result<Unit> = Result.success(Unit)
    override suspend fun cancelSession(sessionId: String): Result<Unit> = Result.success(Unit)
    override suspend fun waiveUnpaidByPatient(patientId: String): Result<Unit> = Result.success(Unit)
    override suspend fun refresh(): Result<Unit> = Result.success(Unit)
}

private val demoPayments = listOf(
    Payment(
        id = "demo-payment-1",
        sessionId = "demo-session-1",
        patientId = "demo-patient-1",
        amount = 300000L,
        method = PaymentMethod.TRANSFER,
        notes = null,
        receivedAt = "2026-04-02T10:30:00Z",
        createdAt = "2026-04-02T10:30:00Z",
    ),
    Payment(
        id = "demo-payment-2",
        sessionId = "demo-session-3",
        patientId = "demo-patient-2",
        amount = 300000L,
        method = PaymentMethod.MERCADOPAGO,
        notes = null,
        receivedAt = "2026-04-05T11:30:00Z",
        createdAt = "2026-04-05T11:30:00Z",
    ),
    Payment(
        id = "demo-payment-3",
        sessionId = "demo-session-4",
        patientId = "demo-patient-3",
        amount = 150000L,
        method = PaymentMethod.CASH,
        notes = "Pago parcial",
        receivedAt = "2026-04-08T16:30:00Z",
        createdAt = "2026-04-08T16:30:00Z",
    ),
)

class DemoPaymentRepository : PaymentRepository {
    override fun getPaymentsByPatient(patientId: String): Flow<List<Payment>> =
        flowOf(demoPayments.filter { it.patientId == patientId })

    override fun getPaymentsBySession(sessionId: String): Flow<List<Payment>> =
        flowOf(demoPayments.filter { it.sessionId == sessionId })

    override suspend fun createPayment(request: CreatePaymentRequest): Result<Payment> =
        Result.success(demoPayments.first())

    override suspend fun refresh(): Result<Unit> = Result.success(Unit)
}

@Suppress("LongMethod")
class DemoDashboardRepository : DashboardRepository {
    override fun getDashboard(month: String): Flow<Result<DashboardSummary>> = flowOf(
        Result.success(DashboardSummary(
            month = month,
            totalCollectedThisMonth = 600000L,
            totalOutstanding = 450000L,
            overdueCount = 1,
            pendingCount = 2,
            paidCount = 2,
            patients = listOf(
                PatientPaymentSummary(
                    patientId = "demo-patient-1",
                    patientName = "Valentina Rodríguez",
                    status = PatientStatus.PENDING,
                    totalOwed = 300000L,
                    lastSessionDate = "2026-04-09T10:00:00Z",
                ),
                PatientPaymentSummary(
                    patientId = "demo-patient-2",
                    patientName = "Martín García",
                    status = PatientStatus.UP_TO_DATE,
                    totalOwed = 0L,
                    lastSessionDate = "2026-04-05T11:00:00Z",
                ),
                PatientPaymentSummary(
                    patientId = "demo-patient-3",
                    patientName = "Luciana Fernández",
                    status = PatientStatus.OVERDUE,
                    totalOwed = 100000L,
                    lastSessionDate = "2026-04-08T16:00:00Z",
                ),
                PatientPaymentSummary(
                    patientId = "demo-patient-4",
                    patientName = "Santiago López",
                    status = PatientStatus.PENDING,
                    totalOwed = 300000L,
                    lastSessionDate = "2026-04-10T09:00:00Z",
                ),
            ),
        )),
    )

    override suspend fun refresh(): Result<Unit> = Result.success(Unit)
}

class DemoReminderRepository : ReminderRepository {
    override fun getPendingReminders(): Flow<List<PendingReminder>> = flowOf(emptyList())
    override suspend fun refresh(): Result<Unit> = Result.success(Unit)
    override suspend fun recordBatchSent(entries: List<ReminderSentEntry>): Result<Int> = Result.success(0)
    override fun getPendingCount(): Flow<Int> = flowOf(0)
}

class DemoNotificationSettingsRepository : NotificationSettingsRepository {
    override val preferences: StateFlow<NotificationPreferences> =
        MutableStateFlow(NotificationPreferences.DEFAULT).asStateFlow()

    override suspend fun setSessionRemindersEnabled(enabled: Boolean) = Unit
    override suspend fun setPaymentRemindersEnabled(enabled: Boolean) = Unit
    override suspend fun setSessionAdvanceMinutes(minutes: Int) = Unit
    override suspend fun setPaymentSchedule(dayOfWeek: Int, hour: Int, minute: Int) = Unit
    override suspend fun setPermissionRequested(requested: Boolean) = Unit
}

class DemoUserLogoutManager : UserLogoutManager {
    override val logoutEventFlow: SharedFlow<LogoutEvent> = MutableSharedFlow()
    override fun logout(userId: Long, reason: LogoutReason) = Unit
    override fun softLogout(userId: Long, reason: LogoutReason) = Unit
}

class DemoAuthService : AuthService {
    override val sessionStatus: Flow<SessionStatus> = flowOf(SessionStatus.Authenticated)
    override val currentUserId: String = "demo-user"
    override val currentUserEmail: String = "demo@cauce.app"
    override suspend fun sendOtp(email: String): Result<Unit> = Result.success(Unit)
    override suspend fun verifyOtp(email: String, code: String): Result<Unit> = Result.success(Unit)
    override suspend fun signOut() = Unit
}
